/**
 * Client-side Redirect URL Validator
 * Validates redirect URLs before navigation to prevent open redirect vulnerabilities
 */

// Allowed internal paths (relative URLs)
const ALLOWED_INTERNAL_PATHS = [
  '/',
  '/admin/dashboard',
  '/admin/login',
  '/admin/users',
  '/admin/news',
  '/admin/blogs',
  '/admin/events',
  '/admin/gallery',
  '/admin/projects',
  '/admin/announcements',
  '/admin/diseases',
  '/admin/services',
  '/admin/senior-management',
  '/admin/donors-partners',
  '/admin/departments',
  '/admin/symptom-browse',
  '/admin/tests-procedures',
  '/admin/activity-logs',
  '/home',
  '/about-us',
  '/news',
  '/blog',
  '/events',
  '/projects',
  '/gallery',
  '/contact',
  '/diseases',
  '/symptoms',
  '/services',
  '/departments-centers'
];

// Allowed external domains (from environment or config)
function getAllowedDomains() {
  try {
    const envVar = process.env.REACT_APP_ALLOWED_REDIRECT_DOMAINS;
    if (envVar && typeof envVar === 'string') {
      return envVar.split(',').map(domain => domain.trim().toLowerCase()).filter(Boolean);
    }
  } catch (error) {
    // Silently fail - environment variables are optional
  }
  return [];
}

/**
 * Validates a redirect URL against the allowlist
 * @param {string} redirectUrl - The URL to validate
 * @returns {boolean} - True if the URL is allowed, false otherwise
 */
export function isValidRedirectUrl(redirectUrl) {
  if (!redirectUrl || typeof redirectUrl !== 'string') {
    return false;
  }

  // Remove whitespace
  const url = redirectUrl.trim();

  // Empty string is not valid
  if (url === '') {
    return false;
  }

  // Check for dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:'];
  const lowerUrl = url.toLowerCase();
  if (dangerousProtocols.some(protocol => lowerUrl.startsWith(protocol))) {
    return false;
  }

  // Check for relative URLs (internal paths)
  if (url.startsWith('/')) {
    // Remove query parameters and hash for path validation
    const path = url.split('?')[0].split('#')[0];
    
    // Check exact match
    if (ALLOWED_INTERNAL_PATHS.includes(path)) {
      return true;
    }
    
    // Check if it's a sub-path of allowed paths (e.g., /blog/123, /news/456)
    const allowedSubPaths = [
      '/blog/',
      '/news/',
      '/events/',
      '/projects/',
      '/diseases/',
      '/diseases/detail/',
      '/services/',
      '/services/detail/',
      '/tests-procedures/',
      '/tests-procedures/detail/',
      '/departments/',
      '/departments-centers/',
      '/senior-management/',
      '/donors-partners/',
      '/about-us/',
      '/symptoms/'
    ];
    
    if (allowedSubPaths.some(subPath => path.startsWith(subPath))) {
      return true;
    }
    
    return false;
  }

  // Check for absolute URLs (external domains)
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    
    // Remove www. prefix for comparison
    const hostnameWithoutWww = hostname.replace(/^www\./, '');
    
    // Check against allowed domains
    try {
      const allowedDomains = getAllowedDomains();
      for (const allowedDomain of allowedDomains) {
        const allowedHostname = allowedDomain.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0].toLowerCase();
        
        if (hostnameWithoutWww === allowedHostname || hostname === allowedHostname) {
          // Additional check: ensure it's HTTP or HTTPS
          if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
            return true;
          }
        }
      }
    } catch (domainError) {
      // If domain checking fails, deny access
      return false;
    }
    
    return false;
  } catch (error) {
    // Invalid URL format
    return false;
  }
}

/**
 * Sanitizes and validates a redirect URL, returning a safe default if invalid
 * @param {string} redirectUrl - The URL to validate
 * @param {string} defaultUrl - Default URL to return if validation fails
 * @returns {string} - Valid redirect URL or default
 */
export function sanitizeRedirectUrl(redirectUrl, defaultUrl = '/') {
  if (isValidRedirectUrl(redirectUrl)) {
    return redirectUrl;
  }
  console.warn(`Invalid redirect URL blocked: ${redirectUrl}, using default: ${defaultUrl}`);
  return defaultUrl;
}

/**
 * Safe navigation function that validates URLs before navigating
 * @param {Function} navigate - React Router navigate function
 * @param {string} url - URL to navigate to
 * @param {string} defaultUrl - Default URL if validation fails
 */
export function safeNavigate(navigate, url, defaultUrl = '/') {
  const validatedUrl = sanitizeRedirectUrl(url, defaultUrl);
  navigate(validatedUrl);
}

/**
 * Validates redirect URL from query parameters
 * @param {URLSearchParams} searchParams - URL search parameters
 * @param {string} defaultUrl - Default URL if no valid redirect found
 * @returns {string} - Valid redirect URL
 */
export function getValidatedRedirectFromQuery(searchParams, defaultUrl = '/') {
  const redirectUrl = searchParams.get('redirect') 
    || searchParams.get('returnUrl') 
    || searchParams.get('return_to');
  
  if (redirectUrl) {
    return sanitizeRedirectUrl(redirectUrl, defaultUrl);
  }
  
  return defaultUrl;
}
